/**
 * Meeting page (§2): header, stage (video + transcript, equal height), tabs, tab content.
 * Overview tab (§2.4, screenshots/01). Other tabs: build steps 3-4.
 */
import { useEffect, useRef } from "react";
import { ArrowRight, Bell, Check, Play } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { meetingStore, useMeeting } from "../stores/meetingStore";
import { useStoreEffect } from "../hooks/useStoreEffect";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { Face } from "../components/shared/Face";
import { Toaster } from "../components/shared/Toaster";
import { LiveNumber } from "../components/meeting/LiveNumber";
import { Timeline } from "../components/meeting/Timeline";
import { TranscriptPanel } from "../components/meeting/TranscriptPanel";
import { VideoStage } from "../components/meeting/VideoStage";
import { MyNotes } from "../components/meeting/MyNotes";
import { LegislationTab, legislationCount } from "../components/meeting/LegislationTab";
import { PeopleTab } from "../components/meeting/PeopleTab";
import { DocumentsTab } from "../components/meeting/DocumentsTab";
import { AskMeeting } from "../components/meeting/AskMeeting";
import { ShareMeeting } from "../components/meeting/ShareMeeting";
import { FollowSelection } from "../components/shared/FollowSelection";
import { List, ListRow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { TabErrorBoundary } from "../components/shared/States";
import { MidPageAd } from "../ads/AdSlot";
import { useNotes } from "../stores/notesStore";
import { followsStore, useFollows } from "../stores/followsStore";
import { norm as bodyKey } from "../meetings/AllMeetings";
import { getViewer, meetingHref, signInToAct } from "../app/viewer";
import { site } from "../app/site";
import { seekTo } from "../components/meeting/vimeoClock";
import { clock, meetingWhen } from "../lib/format";
import { navigate, plainClick } from "../app/router";
import type { AgendaChapter } from "../types";
import type { Outcome } from "../realtime/protocol";
import { onTablistKey } from "../hooks/useTabs";
import { sectionCrumb, useCrumbs } from "../app/crumbs";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "legislation", label: "Legislation" },
  { id: "people", label: "People" },
  { id: "notes", label: "My notes" },
  { id: "documents", label: "Documents" },
] as const;
type TabId = (typeof TABS)[number]["id"];
/** My notes needs dashboard access (the public meeting page shows the others). */
const tabsFor = () => TABS.filter((t) => t.id !== "notes" || getViewer().admitted);

/** "Oct 14" this year, "Apr 10, 2024" before (the breadcrumb). */
const shortDay = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { timeZone: site.timeZone, month: "short", day: "numeric", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }) });
};

const RESULT: Record<Outcome["result"], { label: string; tone: string }> = {
  passed: { label: "Passed", tone: "positive" },
  failed: { label: "Failed", tone: "negative" },
  not_called: { label: "Not called", tone: "warning" },
  pending: { label: "Pending", tone: "neutral" },
};
const AGENDA_OUTCOME: Record<NonNullable<AgendaChapter["outcome"]>, { label: string; tone: string }> = {
  passed: { label: "Passed", tone: "positive" },
  discussed: { label: "Discussed", tone: "neutral" },
  not_called: { label: "Not called", tone: "warning" },
  introduced: { label: "Introduced", tone: "neutral" },
};

function FollowBody({ body }: { body: string }) {
  const follow = useFollows((s) => s.follows.find((f) => f.type === "committee" && bodyKey(f.refId || f.label) === bodyKey(body)));
  const onClick = () => {
    if (!getViewer().admitted) return signInToAct();
    if (follow) void followsStore.getState().unfollow(follow.id);
    else void followsStore.getState().follow({ type: "committee", label: body, refId: body });
  };
  return (
    <button className={follow ? "btn" : "btn btn--primary"} aria-pressed={!!follow} onClick={onClick}>
      {follow ? <Check size={14} /> : <Bell size={14} />}{follow ? "Following" : "Follow"}
    </button>
  );
}

/**
 * "← All meetings": back to where the reader came from when that was this site (history.back keeps their place in the
 * meetings list), else a plain link to the site's meetings (the dashboard's own inside /dashboard).
 */
export function BackToMeetings() {
  const href = getViewer().publicPage ? "/meetings" : "/dashboard/meetings";
  const onClick = (e: React.MouseEvent) => {
    if (!plainClick(e)) return;
    let same = false;
    try { same = !!document.referrer && new URL(document.referrer).origin === location.origin; } catch { /* no referrer */ }
    if (same && history.length > 1) { e.preventDefault(); history.back(); }
  };
  return <a className="back-link meeting-back" href={href} onClick={onClick}>← All meetings</a>;
}

function Header() {
  const meeting = useMeeting((s) => s.meeting)!;
  return (
    <header className="meeting-header">
      <div className="title-block">
        <BackToMeetings />
        <div className="title-row">
          <h1 className="title">{meeting.body}</h1>
          {meeting.status === "live" && <span className="live-chip"><i />LIVE</span>}
          <ConnectionStatus />
        </div>
        <p className="meta">{meetingWhen(meeting.startsAt)}{meeting.location ? ` · ${meeting.location}` : ""}</p>
      </div>
      <div className="actions">
        <ShareMeeting />
        {site.accounts && <FollowBody body={meeting.body} />}
      </div>
    </header>
  );
}

function Tabs({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }) {
  const notes = useNotes((s) => s.notes.filter((n) => n.mine !== false).length);
  const counts = { ...useMeeting(useShallow((s) => ({
    legislation: legislationCount(s),
    people: s.report?.whoSpoke.length ?? 0,
    documents: s.meeting?.documents.length ?? 0,
  }))), notes };
  return (
    <div className="tabs" role="tablist" aria-label="Meeting" onKeyDown={onTablistKey}>
      {tabsFor().map((t) => {
        const n = t.id === "overview" ? 0 : counts[t.id];
        return (
          <button key={t.id} role="tab" className="tab" aria-selected={active === t.id} onClick={() => onChange(t.id)}>
            {t.label}
            {n > 0 && <span className="count-badge">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

function Outcomes() {
  const outcomes = useMeeting((s) => s.outcomes).slice(0, 3);
  if (!outcomes.length) return null;
  return (
    <div className="outcomes">
      {outcomes.map((o) => {
        const r = RESULT[o.result];
        const voted = o.yes + o.no > 0;
        return (
          <div className="outcome" key={o.id}>
            <div className="outcome-value">
              <span className="vote" data-empty={voted ? undefined : ""}>
                {voted ? (
                  <LiveNumber
                    select={(s) => { const x = s.outcomes.find((y) => y.id === o.id); return x && x.yes + x.no > 0 ? `${x.yes}–${x.no}` : undefined; }}
                  />
                ) : "—"}
              </span>
              <span className={`chip chip--${r.tone}`}>{r.label}</span>
            </div>
            <p className="outcome-label">{o.label}</p>
          </div>
        );
      })}
    </div>
  );
}

function WhoSpoke({ onOpen }: { onOpen: () => void }) {
  const { report, people } = useMeeting(useShallow((s) => ({ report: s.report, people: s.people })));
  if (!report || !report.whoSpoke.length) return null;
  const shown = report.whoSpoke.slice(0, 6);
  const more = report.whoSpoke.length - shown.length;
  return (
    <div className="people-preview">
      <div className="preview-head">
        Who spoke
        <button className="link-arrow" onClick={onOpen}>See where everyone stood<ArrowRight size={14} /></button>
      </div>
      <div className="preview-row">
        <div className="avatars">
          {shown.map((w) => {
            const p = people[w.personId];
            return (
              <span key={w.personId} className="face-wrap" data-stance={w.stance}>
                <Face name={p?.name ?? "Speaker"} photoUrl={p?.photoUrl} size={30} />
              </span>
            );
          })}
          {more > 0 && <span className="more">+{more}</span>}
        </div>
        <p className="preview-note">
          {report.alderpersonCount} {site.members} and {report.publicCommenters} public commenters.
          {report.stanceTopic ? ` Ring color shows their stance on ${report.stanceTopic}.` : ""}
        </p>
      </div>
    </div>
  );
}

/** A moment's own link: the public page plays from ?t= (main.tsx), and a plain click seeks in place. Google's video
 *  chapters (the page's VideoObject hasPart) need these links on the page. */
const momentHref = (meetingId: string, start: number) => `${meetingHref(meetingId)}?t=${Math.floor(start)}`;

function Agenda() {
  const chapters = useMeeting((s) => s.chapters).filter((c) => !c.procedural);
  const meetingId = useMeeting((s) => s.meeting?.id ?? "");
  const listRef = useRef<HTMLDivElement>(null);
  // The playing item follows the video (rarely changes): data attribute, no re-render.
  useStoreEffect(meetingStore, (s) => {
    let at = -1;
    s.chapters.forEach((c) => { if (!c.procedural && c.start <= s.currentTime) at = c.start; });
    return at;
  }, (start) => {
    listRef.current?.querySelectorAll<HTMLElement>(".agenda-row").forEach((el) => el.toggleAttribute("data-playing", Number(el.dataset.start) === start));
  });
  if (!chapters.length) return null;
  return (
    <div className="agenda" ref={listRef}>
      <h3 className="agenda-heading">On the agenda</h3>
      <List className="agenda-list">
        {chapters.map((c) => {
          const o = c.outcome ? AGENDA_OUTCOME[c.outcome] : null;
          const ids = c.legislationIds ?? [];
          return (
            <ListRow key={c.start} className="agenda-row" data-start={c.start} href={momentHref(meetingId, c.start)} onOpen={() => seekTo(c.start)}>
              <RowLead className="agenda-time"><Play className="playing-icon" size={10} fill="currentColor" strokeWidth={0} />{clock(c.start)}</RowLead>
              <RowMain min={140} className="agenda-item">
                <RowTitle lines={3} className="agenda-title">{c.title}</RowTitle>
                {ids.length === 1 && <RowSub className="agenda-id">{ids[0]}</RowSub>}
                {ids.length > 1 && <RowSub className="agenda-id">{ids.length} items</RowSub>}
              </RowMain>
              {o && <RowTrail><span className={`chip chip--${o.tone} chip--medium`}>{o.label}</span></RowTrail>}
            </ListRow>
          );
        })}
      </List>
    </div>
  );
}

/** Public meeting page: what each member said, at its time in the video (meeting.keyMoments, from the server). */
function KeyMoments() {
  const meeting = useMeeting((s) => s.meeting);
  const moments = meeting?.keyMoments ?? [];
  if (!meeting || !moments.length) return null;
  return (
    <div className="agenda key-moments">
      <h3 className="agenda-heading">Speaker moments</h3>
      <List className="agenda-list">
        {moments.map((m) => (
          <ListRow key={`${m.start}-${m.title}`} className="agenda-row" href={momentHref(meeting.id, m.start)} onOpen={() => seekTo(m.start)}>
            <RowLead className="agenda-time">{clock(m.start)}</RowLead>
            <RowMain min={140} className="agenda-item"><RowTitle lines={3} className="agenda-title">{m.title}</RowTitle></RowMain>
          </ListRow>
        ))}
      </List>
    </div>
  );
}

/** The public page's mid-page ad goes in only when the agenda and speaker moments below it run long (a screen or more),
 *  so it never sits right above the leaderboard after the meeting (main.tsx). */
export const MID_AD_MIN_ROWS = 8;

function Overview({ onOpenPeople, ads }: { onOpenPeople: () => void; ads: boolean }) {
  const report = useMeeting((s) => s.report);
  const rows = useMeeting((s) => s.chapters.filter((c) => !c.procedural).length + (s.meeting?.keyMoments?.length ?? 0));
  return (
    <div className="overview tab-panel" role="tabpanel">
      <section className="impact">
        <div className="section-head">
          <h2 className="section-title">Impact report</h2>
          <span className="chip chip--neutral chip--medium">Summarized from the transcript</span>
        </div>
        <AskMeeting />
        <Outcomes />
        {report?.summary && <p className="summary" data-follow-source>{report.summary}</p>}
        {!!report?.topics.length && (
          <div className="topics">{report.topics.map((t) => <span key={t} className="topic-chip">{t}</span>)}</div>
        )}
        <WhoSpoke onOpen={onOpenPeople} />
      </section>
      {ads && rows >= MID_AD_MIN_ROWS ? <MidPageAd /> : null}
      <Agenda />
      <KeyMoments />
    </div>
  );
}

export function MeetingPage({ tab, onTab, teamAccount, ads = false }: { tab: string | null; onTab: (t: string) => void; teamAccount: boolean; ads?: boolean }) {
  const meeting = useMeeting((s) => s.meeting);
  const hidden = useMeeting((s) => s.transcriptHidden);
  const active = (tabsFor().some((t) => t.id === tab) ? tab : "overview") as TabId;
  useEffect(() => { if (meeting) document.title = `${meeting.body} · Meetings · ${site.name}`; }, [meeting]);
  useCrumbs(meeting ? [sectionCrumb("meetings"), { label: `${meeting.body} · ${shortDay(meeting.startsAt)}` }] : null);
  if (!meeting) return null;
  const duration = meeting.video?.duration ?? Math.max(1, ...meetingStore.getState().segments.map((s) => s.end));
  return (
    <>
      <Header />
      <section className="stage" data-transcript={hidden ? "hidden" : "open"}>
        <VideoStage duration={duration} />
        <TranscriptPanel canShare={teamAccount} onHide={() => meetingStore.getState().setTranscriptHidden(true)} />
        <Timeline duration={duration} onSeek={seekTo} />
      </section>
      <Tabs active={active} onChange={(t) => onTab(t)} />
      {/* A tab that fails errors on its own; the header, stage and other tabs keep working (screenshots/54 state 8). */}
      <TabErrorBoundary tabLabel={TABS.find((t) => t.id === active)!.label} fallbackHref={active === "documents" ? meeting.documentsSourceUrl : null}>
        {active === "overview" && <Overview onOpenPeople={() => onTab("people")} ads={ads} />}
        {active === "legislation" && <LegislationTab />}
        {active === "people" && <PeopleTab />}
        {active === "documents" && <DocumentsTab />}
        {active === "notes" && <MyNotes />}
      </TabErrorBoundary>
      <FollowSelection />
      <Toaster />
    </>
  );
}
